/**
 * Steuernummer im vereinheitlichten Bundesschema (13 Stellen) für ELSTER:
 * `Landesnummer + Finanzamtsnummer + 0 + Bezirksnummer + Unterscheidungsnummer/Prüfziffer`.
 *
 * Eingabe ist das Landesformat (z. B. BY 181/815/08159), Ausgabe 9181081508159 (T19).
 * Falsche Ziffernzahl → Error mit deutscher Meldung (das Setup zeigt sie unter dem Feld).
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import type {BundeslandCode} from '../../../../helpers/werktage';

export interface SteuernummerScheme {
    prefix: string;                   // Landesnummer im Bundesschema
    blocks: [number, number, number]; // Ziffern je Block im Landesformat (Finanzamt / Bezirk / Unterscheidung+Prüfziffer)
    format: string;                   // Anzeigeformat für die Live-Vorschau
}

const short: SteuernummerScheme['blocks'] = [2, 3, 5];
const long: SteuernummerScheme['blocks'] = [3, 3, 5];

export const STEUERNUMMER_SCHEMES: Record<BundeslandCode, SteuernummerScheme> = {
    BW: {prefix: '28', blocks: short, format: 'FF/BBB/UUUUP'},
    BY: {prefix: '9', blocks: long, format: 'FFF/BBB/UUUUP'},
    BE: {prefix: '11', blocks: short, format: 'FF/BBB/UUUUP'},
    BB: {prefix: '3', blocks: long, format: 'FFF/BBB/UUUUP'},
    HB: {prefix: '24', blocks: short, format: 'FF/BBB/UUUUP'},
    HH: {prefix: '22', blocks: short, format: 'FF/BBB/UUUUP'},
    HE: {prefix: '26', blocks: long, format: '0FF/BBB/UUUUP'},
    MV: {prefix: '4', blocks: long, format: 'FFF/BBB/UUUUP'},
    NI: {prefix: '23', blocks: short, format: 'FF/BBB/UUUUP'},
    NW: {prefix: '5', blocks: [3, 4, 4], format: 'FFF/BBBB/UUUP'},
    RP: {prefix: '27', blocks: short, format: 'FF/BBB/UUUUP'},
    SL: {prefix: '1', blocks: long, format: 'FFF/BBB/UUUUP'},
    SN: {prefix: '3', blocks: long, format: 'FFF/BBB/UUUUP'},
    ST: {prefix: '3', blocks: long, format: 'FFF/BBB/UUUUP'},
    SH: {prefix: '21', blocks: short, format: 'FF/BBB/UUUUP'},
    TH: {prefix: '4', blocks: long, format: 'FFF/BBB/UUUUP'},
};

export const steuernummerFormat = (bundesland: BundeslandCode): string => STEUERNUMMER_SCHEMES[bundesland].format;

export const toElsterSteuernummer = (steuernummer: string, bundesland: BundeslandCode): string => {
    const scheme = STEUERNUMMER_SCHEMES[bundesland];
    if (!scheme) {
        throw new Error(`Unbekanntes Bundesland: ${bundesland}`);
    }
    const digits = (steuernummer ?? '').replace(/\D/g, '');
    const expected = scheme.blocks[0] + scheme.blocks[1] + scheme.blocks[2];
    if (digits.length !== expected) {
        throw new Error(`Steuernummer für ${bundesland} erwartet ${expected} Ziffern (${scheme.format}), erhalten ${digits.length}.`);
    }
    let finanzamt = digits.slice(0, scheme.blocks[0]);
    const bezirk = digits.slice(scheme.blocks[0], scheme.blocks[0] + scheme.blocks[1]);
    const unterscheidung = digits.slice(scheme.blocks[0] + scheme.blocks[1]);
    if (bundesland === 'HE') {
        if (finanzamt[0] !== '0') {
            throw new Error('Steuernummer für Hessen beginnt mit 0 (Format 0FF/BBB/UUUUP).');
        }
        finanzamt = finanzamt.slice(1);
    }
    return `${scheme.prefix}${finanzamt}0${bezirk}${unterscheidung}`;
};

export const isValidElsterSteuernummer = (value: string): boolean => /^\d{13}$/.test(value ?? '') && value[4] === '0';
